import React, { useEffect, useRef, useState } from 'react';
import Grid from "@mui/material/Grid2";
import {
    Box,
    Card,
    CardContent,
    IconButton,
    Stack,
    Typography,
    useColorScheme,
} from "@mui/material";
import {
    DarkMode,
    LightMode,
} from "@mui/icons-material";
import { checkMobileValid, fixFarsiNumbers, fixMobile } from "../../helper/utils";
import { login, sendSms } from "../../network/api/account.api";
import { connect } from "react-redux";
import { authActions } from "../../helper/redux/actions/AuthActions";
import { useNavigate } from "react-router";
import _LoginHeroColumn from "./partials/_LoginHeroColumn";
import _LoginPhoneStep from "./partials/_LoginPhoneStep";
import _LoginOtpStep from "./partials/_LoginOtpStep";

const STATUS_ENUM = {
    CLEAR: 0,
    LOADING_SEND_SMS: 1,
    SMS_SENT: 2,
    LOADING_CONFIRM: 3,
};

const NewLogin = (props) => {
    const navigate = useNavigate();
    const { mode, setMode } = useColorScheme();
    const [status, setStatus] = useState(STATUS_ENUM.CLEAR);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [code, setCode] = useState("");
    const [smsError, setSmsError] = useState(null);
    const [codeError, setCodeError] = useState(null);
    const [canSendCode, setCanSendCode] = useState(false);
    const [canConfirmCode, setCanConfirmCode] = useState(false);
    const [timerCount, setTimerCount] = useState(120);
    const timerRef = useRef(null);

    useEffect(() => {
        if (status === STATUS_ENUM.SMS_SENT) {
            setTimerCount(120);
            if (timerRef.current) clearInterval(timerRef.current);

            timerRef.current = setInterval(() => {
                setTimerCount((prev) => {
                    if (prev <= 1) {
                        clearInterval(timerRef.current);
                        timerRef.current = null;
                        setStatus(STATUS_ENUM.CLEAR);
                        setCanSendCode(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [status]);

    useEffect(() => {
        try {
            if (window.location.pathname === "/auth/logout") {
                setTimeout(() => {
                    window.location = "/";
                }, 800);
            }
        } catch (e) {}
    }, []);

    useEffect(() => {
        if (mode === 'system') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            setMode(mediaQuery.matches ? 'dark' : 'light');
            const handler = (e) => setMode(e.matches ? 'dark' : 'light');
            mediaQuery.addEventListener('change', handler);
            return () => mediaQuery.removeEventListener('change', handler);
        }
    }, [mode, setMode]);

    if (!mode) {
        return null;
    }

    function submitForm(event) {
        if (event) event.preventDefault();
        const cleanNumber = fixMobile(fixFarsiNumbers(phoneNumber));
        if (!checkMobileValid(cleanNumber)) {
            setSmsError("شماره همراه وارد شده صحیح نیست.");
            return;
        }

        setStatus(STATUS_ENUM.LOADING_SEND_SMS);
        setSmsError(null);

        sendSms({
            phoneNumber: cleanNumber,
            Application: "WEBCORPORATE"
        })
            .then(() => {
                setStatus(STATUS_ENUM.SMS_SENT);
                setSmsError(null);
            })
            .catch((err) => {
                setStatus(STATUS_ENUM.CLEAR);
                setSmsError(err?.response?.data?.Message || "خطا در ارسال پیامک. لطفاً مجدداً تلاش فرمایید.");
            });
    }

    function mobileValidator(number) {
        setSmsError(null);
        const clean = fixFarsiNumbers(number || "");
        setPhoneNumber(clean);
        if (!checkMobileValid(clean)) {
            setCanSendCode(false);
            return "شماره موبایل صحیح نیست";
        }
        setCanSendCode(true);
    }

    function codeValidation(val) {
        setCodeError(null);
        const cleanVal = fixFarsiNumbers(val ? val.toString() : "");
        setCode(cleanVal);
        if (cleanVal.length < 4) {
            setCanConfirmCode(false);
            return "کد ۴ رقمی است";
        }
        setCanConfirmCode(true);
    }

    function confirmCode(enteredCode) {
        const finalCode = fixFarsiNumbers(enteredCode || code);
        if (!finalCode || finalCode.length < 4) return;

        setStatus(STATUS_ENUM.LOADING_CONFIRM);
        setCodeError(null);

        login({
            username: fixMobile(fixFarsiNumbers(phoneNumber)),
            password: finalCode,
            Application: "WEBCORPORATE"
        })
            .then((res) => {
                const userData = res.data.Data;
                props.SetUser(userData);
                props.SetToken(userData.Token);
                props.SetRefreshToken(userData.RefreshToken);
                navigate("/dashboard", { replace: true });
            })
            .catch((err) => {
                setStatus(STATUS_ENUM.SMS_SENT);
                setCodeError(err?.response?.data?.Message || "کد تایید وارد شده نامعتبر یا منقضی است.");
            });
    }

    function handleEditPhoneNumber() {
        if (timerRef.current) clearInterval(timerRef.current);
        setStatus(STATUS_ENUM.CLEAR);
        setCanSendCode(true);
        setCodeError(null);
        setSmsError(null);
    }

    return (
        <Box
            className={mode === 'light' ? "loginBgLight" : "loginBgDark"}
            sx={{
                minHeight: '100vh',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 2, sm: 3, md: 4 }
            }}
        >
            <Grid
                container
                spacing={4}
                alignItems="center"
                justifyContent="center"
                sx={{ maxWidth: 1100, width: '100%' }}
            >
                {/* کارت فرم ورود */}
                <Grid size={{ xs: 12, md: 6, lg: 5 }}>
                    <Card
                        sx={{
                            borderRadius: 4.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            boxShadow: mode === 'dark'
                                ? '0 12px 40px rgba(0, 0, 0, 0.4)'
                                : '0 12px 36px rgba(28, 139, 156, 0.08)',
                            overflow: 'hidden',
                            position: 'relative'
                        }}
                    >
                        {/* هدر بالای کارت: تغییر تم */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', p: 2, pb: 0 }}>
                            <IconButton
                                size="small"
                                onClick={() => setMode(mode === 'light' ? 'dark' : 'light')}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2.5
                                }}
                            >
                                {mode === 'light' ? (
                                    <DarkMode sx={{ fontSize: 20, color: 'text.secondary' }} />
                                ) : (
                                    <LightMode sx={{ fontSize: 20, color: '#f59e0b' }} />
                                )}
                            </IconButton>
                        </Box>

                        <CardContent sx={{ px: { xs: 3, sm: 4.5 }, pb: { xs: 4, sm: 5 } }}>
                            {/* لوگوی جیم‌پین */}
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <Box
                                    component="img"
                                    src={mode === 'light' ? "/logoTypo300.png" : "/logoTypo300Dark.png"}
                                    alt="جیم‌پین اپسا"
                                    sx={{
                                        maxWidth: 240,
                                        width: '100%',
                                        height: 'auto',
                                        objectFit: 'contain'
                                    }}
                                />
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 800,
                                        color: 'text.primary',
                                        mt: 2,
                                        fontSize: '1.2rem'
                                    }}
                                >
                                    ورود به پنل سازمانی
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        display: 'block',
                                        mt: 0.5,
                                        fontSize: '0.82rem'
                                    }}
                                >
                                    سامانه مدیریت تسهیلات و خدمات ورزشی کارکنان
                                </Typography>
                            </Box>

                            <form onSubmit={submitForm}>
                                <Stack spacing={2.5}>
                                    {/* بخش اول: دریافت شماره موبایل */}
                                    <_LoginPhoneStep
                                        status={status}
                                        STATUS_ENUM={STATUS_ENUM}
                                        mobileValidator={mobileValidator}
                                        smsError={smsError}
                                        canSendCode={canSendCode}
                                        onSubmitPhone={submitForm}
                                    />

                                    {/* بخش دوم: ورود کد تایید پیامکی */}
                                    {(status === STATUS_ENUM.SMS_SENT || status === STATUS_ENUM.LOADING_CONFIRM) && (
                                        <_LoginOtpStep
                                            status={status}
                                            STATUS_ENUM={STATUS_ENUM}
                                            phoneNumber={phoneNumber}
                                            handleEditPhoneNumber={handleEditPhoneNumber}
                                            confirmCode={confirmCode}
                                            codeValidation={codeValidation}
                                            codeError={codeError}
                                            canConfirmCode={canConfirmCode}
                                            code={code}
                                            timerCount={timerCount}
                                            onResendSms={() => submitForm()}
                                        />
                                    )}
                                </Stack>
                            </form>
                        </CardContent>
                    </Card>
                </Grid>

                {/* تصویرسازی لاتی و شعار انگیزشی سمت چپ */}
                <_LoginHeroColumn />
            </Grid>
        </Box>
    );
};

export default connect(null, authActions)(NewLogin);
